import React, { useEffect, useMemo, useState } from "react";
import sortUpDownIcon from "../../../../img/icons/sort-up-down.svg";
import PropTypes from "prop-types";
import * as HASH from "../../../../constants/config/hash";
import { resetColumn, resetSort, sortByColumn } from "../../../../actions/filters";
import {
  findHashValue,
  hashToSort,
  sortToHash,
  updateHash,
} from "../../../../utilities/hash";
import sortUpIcon from "../../../../img/icons/SortUp.svg";
import sortDownIcon from "../../../../img/icons/SortDown.svg";
import { track } from "../../../../actions/dtm";
import { usePrevious } from "../../../../utilities/hooks";
import { SORT_COLUMN } from "../../../../constants/config/dtm";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import localization from "../../../../data/config"
import ToolTip from "../../../ToolTip";

/**
 * @name ColumnSort
 * @function
 * @description Displays SortIcons for Column Sorting
 * @returns {JSX}
 * @param {*} props
 */
const ColumnSort = (props) => {
  const CLICK = "click",
    [sorting, setSorting] = useState(null),
    [event, setEvent] = useState(null),
    [ctrl, setCtrl] = useState(false),
    [initial, setInitial] = useState(false),
    prevSorting = usePrevious(sorting);
  const history = useHistory();
  const dispatch = useDispatch();

  const { newSort, sortPriority, resetSignal, modifiedFiltersAry, sortLabel, sortAscLabel, sortDescLabel } = useSelector((state) => ({
    newSort: state.filters.newSort,
    sortPriority: state.filters.sortPriority,
    resetSignal : state.filters.resetSignal,
    modifiedFiltersAry: state.filters.modifiedFiltersAry,
    sortLabel: state.labels.button.sort,
    sortAscLabel: state.labels.button.sortAsc,
    sortDescLabel: state.labels.button.sortDesc
  }));

  useEffect(() => {
    let currentHash = findHashValue(HASH.SORT),
      sortItems = currentHash ? hashToSort(currentHash) : [],
      hash = null,
      index = sortItems.findIndex((item) => item[0] === props.parameter);

    if (sorting !== prevSorting && event === "click") {
      // if this parameter exists, update its value
      if (index > -1) {
        // update the existing filter
        if (sorting) {
          sortItems[index][1] = sorting;

          // remove it if there is no value
        } else {
          sortItems.splice(index, 1);
        }

        // if it doesn't exist, add it when there is a sort value
      } else {
        if (sorting) {
          if (ctrl) {
            sortItems.push([props.parameter, sorting]);
          } else {
            sortItems = [[props.parameter, sorting]];
          }
        }
      }

      if (sortItems.length > 0) hash = sortToHash(sortItems);

      updateHash(HASH.SORT, hash);
      dispatch(resetSort(props.parameter));

      // track sort after hash update so hash value is available in DTM
      if (hash) dispatch(track(SORT_COLUMN));
    } else if (currentHash && sorting) {
      if (index === -1) {
        setSorting(null);
        setEvent(null);
      }

      // initial page load
    } else if (!initial && sortItems.length > 0) {
      //only update when there is a sort
      if (index === -1) return setInitial(true);

      setSorting(sortItems[index][1]);
      setInitial(index + 1);
    }

    // clear sorting on reset
    if (
      history.location.hash === HASH.INITIATOR &&
      sortItems.length === 0
    ) {
      // clear sort priority on reset
      dispatch(sortByColumn(null, null, null, null));
      setSorting(null);
      props.setSortStatus("");
    }
  }, [props.parameter, sorting, ctrl, newSort, history.location.hash]);
  useEffect(() => {
    if (resetSignal && modifiedFiltersAry) {
      modifiedFiltersAry.forEach((filter) => {
        if (filter === props.parameter) {
          if (sorting) {
            // Reset and clear sorting
            dispatch(sortByColumn(props.parameter, null, null, null));
            dispatch(resetColumn(true, props.parameter));
            setSorting(null);
            props.setSortStatus("");
          } else {
            // Only reset without clearing sorting
            dispatch(resetColumn(false, props.parameter));
          }
        }
      });
    }
  }, [resetSignal, sorting]);
  /**
   * @name handleClick
   * @function
   * @description Assigns the sort value to the current parameter
   */
  const handleClick = (e) => {
    if (e.ctrlKey !== ctrl) setCtrl(e.ctrlKey);
    let sortOrder = null;

    switch (sorting) {
      case HASH.SORT_ASCENDING:
        setSorting(HASH.SORT_DESCENDING);
        setEvent(CLICK);
        sortOrder = HASH.SORT_DESCENDING;
        break;
      case HASH.SORT_DESCENDING:
        setSorting(null);
        setEvent(CLICK);
        sortOrder = null;
        break;
      default:
        setSorting(HASH.SORT_ASCENDING);
        setEvent(CLICK);
        sortOrder = HASH.SORT_ASCENDING;
        break;
    }
    props.setSortStatus(sortOrder ? props.parameter : "");
    dispatch(sortByColumn(props.parameter, sortOrder, e.ctrlKey, sortPriority));
  };

    /**
   * @name iconSortUpAndDown
   * @function
   * @description Assigns the sort value depending on the icon, for accessibility
   */

  const iconSortUpAndDown = () => {
    if (sorting === HASH.SORT_ASCENDING) {
      return localization.sortAscending
    } else if (sorting === HASH.SORT_DESCENDING) {
      return localization.sortDescending
    } else {
      return { label: `${props.name} ${localization.sortButton}` };
    }
  }

  const tooltipLabel = useMemo(() => {
    return sorting === HASH.SORT_ASCENDING
    ? sortAscLabel
    : sorting === HASH.SORT_DESCENDING
    ? sortDescLabel
    : sortLabel
  }, [sorting]);

  return (
    <span className={`parts__tooltip-wrapper`}>
      <ToolTip label={tooltipLabel} />
    <img
      src={
        sorting === HASH.SORT_ASCENDING
          ? sortUpIcon
          : sorting === HASH.SORT_DESCENDING
          ? sortDownIcon
          : sortUpDownIcon
      }
      className="header-title__sort-icon"
      tabIndex={0}
      aria-label={`${props.name} ${localization.sorting}`}
      role="button"
      alt="sort-up-down-icon"
      onClick={handleClick}
    />
    <p aria-live="polite" className="sr-only"> {typeof iconSortUpAndDown() === 'string'
    ? iconSortUpAndDown()
    : iconSortUpAndDown().label}</p>
    </span>
  );
};

ColumnSort.propTypes = {
  name: PropTypes.string.isRequired,
  parameter: PropTypes.string.isRequired,
  setSortStatus: PropTypes.func,
};

export default ColumnSort;